import React, { useCallback } from "react";
import { graphql } from "react-relay";

import cx from "classnames";

import { ReactComponent as GSuiteLogo } from "~/legacy-assets/images/logos/gsuite.svg";
import { ReactComponent as MicrosoftLogo } from "~/legacy-assets/images/logos/microsoft.svg";
import { promptGoogleAuth, promptOffice365Auth } from "~/lib/auth";
import { useLazyLoadQuery } from "~/lib/relayUtil";

import { ReactComponent as CirclePattern } from "./css/circle-pattern.svg";
import { ReactComponent as WavePattern } from "./css/wave-pattern.svg";
import { JourneyContext } from "./JourneyContext";

import type { ConnectPageQuery } from "~/__generated__/ConnectPageQuery.graphql";

import stylesOnboarding from "~/onboarding/css/Onboarding.module.css";

import styles from "./css/ConnectPage.module.css";

export const CONNECT_PATH = "/o/connect/";

export default function ConnectPage() {
	const { nextStep } = React.useContext(JourneyContext);

	const props = useLazyLoadQuery<ConnectPageQuery>(
		graphql`
			query ConnectPageQuery {
				viewer {
					user {
						firstName
						fullName
					}
					account {
						integrations {
							gsuite {
								id
							}
							office365 {
								id
							}
						}
					}
				}
			}
		`,
		{},
		{ fetchPolicy: "store-and-network" },
	);

	const onNextStep = useCallback(() => {
		nextStep();
	}, [nextStep]);

	const connectOffice = () => {
		promptOffice365Auth({ refresh: true }).then(onNextStep);
	};

	const connectGoogle = () => {
		promptGoogleAuth({ refresh: true }).then(onNextStep);
	};

	const { user, account } = props.viewer;

	return (
		<>
			<WavePattern className={cx(styles.wavePattern, stylesOnboarding.pattern)} />
			<CirclePattern className={cx(styles.circlePattern, stylesOnboarding.pattern)} />

			<div className={styles.greeting}>Welcome, {user.firstName || user.fullName}!</div>
			<div className={stylesOnboarding.title}>Connect your work calendar with Fellow</div>
			<div className={stylesOnboarding.subtitle}>Fellow links your meeting notes with your calendar events.</div>

			<div className={styles.options}>
				{account.integrations.gsuite?.id && (
					<button className={styles.option} onClick={connectGoogle}>
						<GSuiteLogo />
						<div className={styles.optionText}>Connect Google</div>
					</button>
				)}

				{account.integrations.office365?.id && (
					<button className={styles.option} onClick={connectOffice}>
						<MicrosoftLogo />
						<div className={styles.optionText}>Connect Office</div>
					</button>
				)}
			</div>
		</>
	);
}
